import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { findNodeType } from '@loggy/shared';

import { Badge, Card, IconButton, Stack, Text, cx } from '../design/index.js';
import { daysLabel, formatTarget, formatValue, goalStatus, isMetricOrbitItem } from './OrbitView.js';
import type { GoalStatus, OrbitItem } from './OrbitView.js';
import styles from './NodeRail.module.css';

export interface NodeRailProps {
  items: OrbitItem[];
  onSelect: (nodeId: string) => void;
  /**
   * Fired when the row's own edit affordance is used, rather than the row
   * itself — the two must never both fire from the same click.
   *
   * Optional, and the control itself only renders when it is supplied —
   * omitted (not a no-op) is how a caller viewing someone else's dashboard
   * keeps the pencil out of the DOM entirely, since the server refuses the
   * edit anyway and a disabled button would still be a control offering it.
   */
  onEdit?: (nodeId: string) => void;
  /** Lets the caller place this in a named grid area (see Dashboard.module.css). */
  className?: string;
}

function PencilIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

/**
 * The same items as `OrbitStage`, in words. Not a fallback: it is what makes
 * the data reachable when the scene is too small, too dense, or the viewer
 * would rather read than look — so it renders every item, goal or merely
 * watched, never just the goals the design's own "Goals · by deadline"
 * caption would suggest.
 *
 * The design's own card idiom (title + status meta, a big figure with its
 * target, a progress track) is used as-is for a goal row. A watched item has
 * no target and nothing to progress toward, so its row is deliberately
 * quieter: no coloured border, no track, just the title and the latest
 * reading — the same filled/hollow swatch distinction the scene's markers
 * use carries over here too.
 */
export function NodeRail({ items, onSelect, onEdit, className }: NodeRailProps) {
  return (
    <Stack gap={3} className={cx(styles.list, className)} aria-label="All tracked nodes">
      {items.map((item) => {
        const isGoal = item.goal !== null;
        const status = item.goal ? goalStatus(item.goal) : null;
        const isMetric = isMetricOrbitItem(item);

        const stopThenEdit = onEdit
          ? (event: MouseEvent | KeyboardEvent) => {
              event.stopPropagation();
              onEdit(item.nodeId);
            }
          : undefined;

        return (
          <Card
            key={item.nodeId}
            role="button"
            tabIndex={0}
            className={cx(styles.row, isGoal ? statusRowClass(status!) : styles.rowWatched)}
            data-testid={`orbit-row-${item.nodeId}`}
            onClick={() => onSelect(item.nodeId)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(item.nodeId);
              }
            }}
          >
            <Stack gap={2}>
              <div className={styles.rowHead}>
                <div className={styles.rowTitle}>
                  <span
                    aria-hidden="true"
                    className={cx(styles.swatch, isGoal ? statusSwatchClass(status!) : styles.swatchWatched)}
                  />
                  <Text size="sm" weight="medium">{item.title}</Text>
                </div>
                <div className={styles.rowMeta}>
                  {renderMeta(item, status)}
                  {onEdit && (
                    <IconButton
                      aria-label={`Edit ${item.title}`}
                      size="sm"
                      className={styles.editButton}
                      onClick={stopThenEdit}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
                      }}
                    >
                      <PencilIcon />
                    </IconButton>
                  )}
                </div>
              </div>

              {item.goal && item.goal.title !== item.title && (
                <Text size="xs" color="muted">{item.goal.title}</Text>
              )}

              <div className={styles.rowValue}>
                <Text family="figure" tabular size="md">{formatValue(item)}</Text>
                {item.goal && isMetric && (
                  <Text size="xs" color="secondary">target {formatTarget(item, item.goal)}</Text>
                )}
              </div>

              {item.goal && (
                <div className={styles.track}>
                  <div
                    className={cx(styles.fill, statusFillClass(status!))}
                    style={{ width: `${Math.round((item.goal.progress.fraction ?? 0) * 100)}%` }}
                  />
                </div>
              )}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}

function statusRowClass(status: GoalStatus): string {
  if (status === 'achieved') return styles.rowAchieved!;
  if (status === 'overdue') return styles.rowOverdue!;
  return styles.rowOnTrack!;
}

function statusFillClass(status: GoalStatus): string {
  if (status === 'achieved') return styles.fillAchieved!;
  if (status === 'overdue') return styles.fillOverdue!;
  return styles.fillOnTrack!;
}

function statusSwatchClass(status: GoalStatus): string {
  if (status === 'achieved') return styles.swatchAchieved!;
  if (status === 'overdue') return styles.swatchOverdue!;
  return styles.swatchOnTrack!;
}

function renderMeta(item: OrbitItem, status: GoalStatus | null): ReactNode {
  if (item.kind !== 'metric') {
    return <Badge>{findNodeType(item.typeKey)?.title ?? 'Tracked'}</Badge>;
  }
  if (!item.goal) return <Badge>Watched</Badge>;
  if (status === 'achieved') return <Badge variant="positive">Achieved</Badge>;
  if (status === 'overdue') return <Badge variant="critical">Overdue</Badge>;
  const days = daysLabel(item.goal.progress.daysRemaining);
  return (
    <Text size="xs" color="muted" family="figure" tabular>
      {days ?? 'No deadline'}
    </Text>
  );
}
